import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";

// Saved vocabulary: a clinician's frequently used terms (drug and brand
// names, conditions, colleagues, clinics). They are given to speech
// recognition as hints and to note generation as preferred spellings; a term
// that was not said must never be added to a note.
export const VOCABULARY_MAX_TERMS = 200;
export const VOCABULARY_TERM_MAX = 60;
export const VOCABULARY_SOUNDS_LIKE_MAX = 60;

// Provider limits, measured October 2026 (synthetic audio):
// - Deepgram (nova-3, nova-3-medical, live and pre-recorded) rejects a request
//   whose keyterms exceed 500 tokens. Drug names cost about 2 characters per
//   token, unusual spellings close to 1, so 450 characters stays under the
//   limit for any list; Deepgram also recommends 20-50 terms.
// - OpenAI realtime transcription rejects a prompt over 1024 characters; the
//   file endpoint accepts longer prompts but whisper-1 keeps only 224 tokens.
export const DEEPGRAM_KEYTERM_MAX_TERMS = 50;
export const DEEPGRAM_KEYTERM_MAX_CHARACTERS = 450;
export const TRANSCRIPTION_PROMPT_MAX_CHARACTERS = 1000;

const id = z.int().positive().max(4294967295);
const pathId = z
  .string()
  .regex(/^[1-9][0-9]*$/)
  .transform(Number)
  .pipe(id);

// Letters, digits, spaces and common punctuation in names (hyphen,
// apostrophe, period, slash, parentheses, ampersand, plus). No line breaks or
// angle brackets: providers reject them in hints.
const ALLOWED = /^[\p{L}\p{M}\p{N} '’.\-/()&+]+$/u;
const text = (max: number) =>
  z
    .string()
    .transform((value) => value.normalize("NFC").replace(/\s+/g, " ").trim())
    .pipe(
      z
        .string()
        .min(1)
        .refine(
          (value) => Array.from(value).length <= max,
          `At most ${max} characters`,
        )
        .refine((value) => ALLOWED.test(value), "Unsupported characters")
        .refine((value) => /\p{L}/u.test(value), "A letter is required"),
    );
export const vocabularyTermTextSchema = text(VOCABULARY_TERM_MAX);
export const vocabularySoundsLikeSchema = text(VOCABULARY_SOUNDS_LIKE_MAX);

export const vocabularyTermInputSchema = z.strictObject({
  term: vocabularyTermTextSchema,
  soundsLike: vocabularySoundsLikeSchema.nullable().optional(),
});
export type VocabularyTermInput = z.output<typeof vocabularyTermInputSchema>;
export const vocabularyAddSchema = z.strictObject({
  terms: z.array(vocabularyTermInputSchema).min(1).max(VOCABULARY_MAX_TERMS),
});
export const vocabularyUpdateSchema = z
  .strictObject({
    term: vocabularyTermTextSchema.optional(),
    soundsLike: vocabularySoundsLikeSchema.nullable().optional(),
  })
  .refine(
    (value) => value.term !== undefined || value.soundsLike !== undefined,
    "An edit is required",
  );
export type VocabularyUpdate = z.output<typeof vocabularyUpdateSchema>;

export const vocabularyTermSchema = z.object({
  id,
  term: z
    .string()
    .min(1)
    .max(VOCABULARY_TERM_MAX * 4),
  soundsLike: z
    .string()
    .min(1)
    .max(VOCABULARY_SOUNDS_LIKE_MAX * 4)
    .nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type VocabularyTerm = z.output<typeof vocabularyTermSchema>;
export const vocabularyListSchema = z.object({
  items: z.array(vocabularyTermSchema).max(VOCABULARY_MAX_TERMS),
});
export const vocabularyAddResultSchema = z.object({
  items: z.array(vocabularyTermSchema).max(VOCABULARY_MAX_TERMS),
  // Terms already in the list (any case or accents) are skipped.
  added: z.int().nonnegative(),
  skipped: z.int().nonnegative(),
});

const errors = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  NOT_FOUND: { message: "Vocabulary term not found" },
  CONFLICT: { message: "Vocabulary conflict" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  SERVICE_UNAVAILABLE: { message: "Saved vocabulary unavailable" },
});
const query = z.strictObject({}).default({});
const params = z.strictObject({ termId: pathId });
const meta = (
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  successStatus = 200,
) =>
  openapi({
    method,
    path: `/api/v2${path}`,
    inputStructure: "detailed",
    successStatus,
    spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
  });

export const vocabularyContract = {
  listVocabulary: errors
    .input(z.object({ query }))
    .meta(meta("GET", "/vocabulary"))
    .output(vocabularyListSchema),
  addVocabulary: errors
    .input(z.object({ query, body: vocabularyAddSchema }))
    .meta(meta("POST", "/vocabulary", 201))
    .output(vocabularyAddResultSchema),
  updateVocabularyTerm: errors
    .input(z.object({ params, query, body: vocabularyUpdateSchema }))
    .meta(meta("PATCH", "/vocabulary/{termId}"))
    .output(vocabularyTermSchema),
  deleteVocabularyTerm: errors
    .input(z.object({ params, query, body: z.undefined().optional() }))
    .meta(meta("DELETE", "/vocabulary/{termId}"))
    .output(z.object({ id })),
};

/** Case- and accent-insensitive key, as the database compares terms. */
export const vocabularyKey = (term: string) =>
  term
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLocaleLowerCase("en")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Bulk paste: one term per line. List markers ("-", "•", "1.") are removed,
 * blank lines and repeats are dropped. Lines that are not valid terms are
 * returned separately so the user can fix them.
 */
export const parseVocabularyPaste = (value: string) => {
  const terms: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  for (const line of value.split(/\r?\n/)) {
    const cleaned = line
      .replace(/^\s*(?:[-*•·–]|\d{1,3}[.)])\s+/u, "")
      .replace(/[,;]+\s*$/, "")
      .trim();
    if (!cleaned) continue;
    const parsed = vocabularyTermTextSchema.safeParse(cleaned);
    if (!parsed.success) {
      invalid.push(cleaned);
      continue;
    }
    const key = vocabularyKey(parsed.data);
    if (seen.has(key)) continue;
    seen.add(key);
    terms.push(parsed.data);
  }
  return { terms, invalid };
};

export type VocabularyEntry = { term: string; soundsLike?: string | null };

const uniqueTerms = (entries: readonly VocabularyEntry[]) => {
  const seen = new Set<string>();
  const terms: string[] = [];
  for (const { term } of entries) {
    const parsed = vocabularyTermTextSchema.safeParse(term);
    if (!parsed.success) continue;
    const key = vocabularyKey(parsed.data);
    if (seen.has(key)) continue;
    seen.add(key);
    terms.push(parsed.data);
  }
  return terms;
};

/**
 * Deepgram keyterms in list order (callers pass the newest first), within
 * the term and character budget. Terms that do not fit are left out.
 */
export const deepgramKeyterms = (
  entries: readonly VocabularyEntry[],
  {
    maxTerms = DEEPGRAM_KEYTERM_MAX_TERMS,
    maxCharacters = DEEPGRAM_KEYTERM_MAX_CHARACTERS,
  } = {},
) => {
  const terms: string[] = [];
  let characters = 0;
  for (const term of uniqueTerms(entries)) {
    if (terms.length >= maxTerms) break;
    const size = Array.from(term).length;
    if (characters + size > maxCharacters) continue;
    characters += size;
    terms.push(term);
  }
  return terms;
};

/**
 * An OpenAI transcription prompt: the existing base text, then the terms
 * that may come up, within maxCharacters. Without terms the base is
 * returned unchanged.
 */
export const transcriptionPromptWithVocabulary = (
  base: string,
  entries: readonly VocabularyEntry[],
  maxCharacters = TRANSCRIPTION_PROMPT_MAX_CHARACTERS,
) => {
  const lead = base.replace(/[.\s]+$/, "");
  const prefix = `${lead ? `${lead}. ` : ""}Terms that may come up: `;
  let prompt = "";
  for (const term of uniqueTerms(entries)) {
    const next = prompt ? `${prompt}, ${term}` : term;
    if (Array.from(prefix + next).length + 1 > maxCharacters) continue;
    prompt = next;
  }
  return prompt ? `${prefix}${prompt}.` : base;
};
